import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, AlertCircle, Loader2, Award, LinkedinIcon, User } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnhancedRecommendationSubmitPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  
  const [requestInfo, setRequestInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [relationshipType, setRelationshipType] = useState('');
  const [whyRecommend, setWhyRecommend] = useState('');
  const [knownSince, setKnownSince] = useState('');
  const [recommendationText, setRecommendationText] = useState('');

  const relationshipOptions = [
    'Peer',
    'Colleague',
    'Candidate',
    'Trainer',
    'Manager',
    'Others'
  ];

  useEffect(() => {
    fetchRequestInfo();
  }, [token]);

  const fetchRequestInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/recommendations/request/${token}`);
      if (response.ok) {
        const data = await response.json();
        setRequestInfo(data);
      } else {
        toast({ 
          title: 'Error', 
          description: 'Invalid or expired recommendation link', 
          variant: 'destructive' 
        });
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to load recommendation request', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInAuth = () => {
    // Redirect to LinkedIn login
    window.location.href = `${BACKEND_URL}/api/auth/linkedin`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast({
        title: 'LinkedIn Authentication Required',
        description: 'Please sign in with LinkedIn to submit your recommendation',
        variant: 'destructive'
      });
      return;
    }

    // Validation
    if (!relationshipType || !whyRecommend.trim() || !knownSince.trim() || !recommendationText.trim()) {
      toast({ 
        title: 'Required Fields', 
        description: 'Please fill in all required fields', 
        variant: 'destructive' 
      });
      return;
    }

    if (recommendationText.trim().length < 50) {
      toast({
        title: 'Recommendation Too Short',
        description: 'Please write at least 50 characters',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('relationship_type', relationshipType);
      formData.append('why_recommend', whyRecommend);
      formData.append('known_since', knownSince);
      formData.append('recommendation_text', recommendationText);

      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(
        `${BACKEND_URL}/api/recommendations/submit/${token}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${authToken}`
          },
          body: formData
        }
      );

      if (response.ok) {
        setSubmitted(true);
        toast({ 
          title: 'Success!', 
          description: 'Thank you for your recommendation',
          duration: 5000
        });
      } else {
        const error = await response.json();
        toast({ 
          title: 'Error', 
          description: error.detail || 'Failed to submit recommendation', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to submit recommendation. Please try again.', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-green-500">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-10 h-10 text-green-600" />
                <div>
                  <CardTitle className="text-2xl text-green-900">
                    Recommendation Submitted!
                  </CardTitle>
                  <CardDescription className="text-green-700 mt-1">
                    Thank you for supporting {requestInfo?.user_name}'s certification
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-slate-700 mb-4">
                Your recommendation has been successfully submitted and will be reviewed as part of 
                {requestInfo?.user_name}'s Bronze TA Certification application.
              </p>
              <p className="text-slate-600 text-sm">
                {requestInfo?.user_name} needs {requestInfo?.recommendations_required || 3} total recommendations. 
                They currently have {(requestInfo?.recommendations_received || 0) + 1} recommendation(s).
              </p>
              <div className="mt-6 flex gap-3">
                <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
                  Go to RecruiterIQ Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!requestInfo) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-red-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-900">
                <AlertCircle className="w-6 h-6" />
                Invalid Link
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700">
                This recommendation link is invalid or has expired. Please contact the person who shared this link.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Check if already completed
  if (requestInfo.status === 'completed' && requestInfo.recommendations_received >= 3) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Award className="w-6 h-6" />
                Recommendation Request Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700">
                {requestInfo.user_name} has already received all required recommendations and their certificate has been issued. Thank you for your interest!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* LinkedIn Auth Required */}
        {!isAuthenticated && (
          <Card className="border-2 border-blue-500 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <LinkedinIcon className="w-10 h-10 text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-bold text-blue-900 mb-2">LinkedIn Authentication Required</h3>
                  <p className="text-blue-800 mb-4">
                    To maintain the integrity of our certification process, all recommendations must be 
                    submitted through verified LinkedIn accounts.
                  </p>
                  <Button 
                    onClick={handleLinkedInAuth}
                    className="bg-[#0077b5] hover:bg-[#006399] text-white"
                  >
                    <LinkedinIcon className="w-4 h-4 mr-2" />
                    Sign in with LinkedIn
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TA Professional Info */}
        <Card className="border-l-4 border-l-blue-600">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-xl text-slate-900">
                    Recommend {requestInfo.user_name}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    For Bronze TA Professional Certification
                  </CardDescription>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 border-green-300 px-4 py-2 text-base">
                <Award className="w-4 h-4 mr-2" />
                Score: {requestInfo.exam_score?.toFixed(1)}%
                </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-2">
                <strong>{requestInfo.user_name}</strong> has successfully passed the Bronze TA Certification exam 
                with a score of <strong>{requestInfo.exam_score?.toFixed(1)}%</strong> and is seeking professional 
                recommendations to complete their certification.
              </p>
              <p className="text-sm text-slate-600">
                Recommendations received: <strong>{requestInfo.recommendations_received || 0} / 3</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Recommendation Form */}
        <Card>
          <CardHeader>
            <CardTitle>Submit Your Recommendation</CardTitle>
            <CardDescription>
              Please answer the following questions about {requestInfo.user_name}'s professional capabilities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question 1: Relationship Type */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  1. How do you know {requestInfo.user_name}? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {relationshipOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setRelationshipType(option)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        relationshipType === option
                          ? 'border-blue-600 bg-blue-50 text-blue-900 font-semibold'
                          : 'border-slate-300 hover:border-blue-400 text-slate-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Question 2: Why Recommend */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  2. Why do you recommend {requestInfo.user_name} as a good TA professional? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={whyRecommend}
                  onChange={(e) => setWhyRecommend(e.target.value)}
                  placeholder="Describe their key strengths, skills, and what makes them effective in talent acquisition..."
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none min-h-[100px]"
                  disabled={!isAuthenticated}
                  required
                />
              </div>

              {/* Question 3: Known Since */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  3. Since when do you know this TA professional? <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={knownSince}
                  onChange={(e) => setKnownSince(e.target.value)}
                  placeholder="e.g., '2 years', '6 months', '3 years and 4 months'"
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  disabled={!isAuthenticated}
                  required
                />
              </div>

              {/* Question 4: Detailed Recommendation */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  4. Write a few lines about why you recommend this TA professional <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={recommendationText}
                  onChange={(e) => setRecommendationText(e.target.value)}
                  placeholder="Share specific examples of their work, achievements, or qualities that demonstrate their expertise in talent acquisition..."
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none min-h-[120px]"
                  disabled={!isAuthenticated}
                  required
                />
                <p className="text-sm text-slate-500 mt-1">
                  Minimum 50 characters. Current: {recommendationText.length} characters
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={!isAuthenticated || submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting Recommendation...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Submit Recommendation
                    </>
                  )}
                </Button>
                
                {!isAuthenticated && (
                  <p className="text-center text-sm text-slate-600 mt-3">
                    Please sign in with LinkedIn to submit your recommendation
                  </p>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Privacy Notice */}
        <Card className="bg-slate-50 border-slate-300">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-600 text-center">
              🔒 Your recommendation is verified through LinkedIn authentication and will be stored securely. 
              It will be used as part of {requestInfo.user_name}'s certification evaluation.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnhancedRecommendationSubmitPage;
